import { useCallback, useMemo } from 'react'
import { Select, Table, Typography, type TableColumnsType } from 'antd'
import type { JSONPatchOperation } from 'immutable-json-patch'
import type { StructuredValue } from '../config/structured'
import { fieldRows, requirementChanges, REQUIREMENTS, type FieldRow, type Requirement } from '../config/fields'
import { fieldControl, fieldControlChanges, fieldSections, type FieldControlEdit } from '../config/field-controls'
import type { StructuredField } from '../config/groups'
import { FieldValue } from './FieldValue'
import './fields-editor.css'

type Props = { value: StructuredValue; disabled: boolean; onApply: (operations: JSONPatchOperation[]) => void; onAdvanced?: () => void }
const REQUIREMENT_OPTIONS = [...REQUIREMENTS]
const SCROLL = { x: 740 }
const LOCALE = { emptyText: '暂无字段' }
const RowValue = ({ row, field, value, onApply, disabled, label }: Props & { row: FieldRow; field: StructuredField; label?: string }) => {
  const edit = useCallback((command: FieldControlEdit) => {
    onApply(fieldControlChanges(value, row.key, field, command))
  }, [field, onApply, row.key, value])
  const control = useMemo(() => fieldControl(value, field), [field, value])
  return <FieldValue label={label ?? row.key + '的' + field.label} control={control} onEdit={edit} disabled={disabled} />
}
const RequirementValue = ({ row, value, onApply, disabled }: Props & { row: FieldRow }) => {
  const change = useCallback((target: Requirement) => onApply(requirementChanges(value, row.key, target)), [onApply, row.key, value])
  return <Select aria-label={row.key + '的基础填写要求'} value={row.requirement} options={REQUIREMENT_OPTIONS}
    placeholder="未设置" disabled={disabled || !row.requirementEditable} onChange={change} className="instance-administration__requirement" />
}

/** This sheet emits guarded source patches through StructuredEditor's existing lifecycle and history owner. */
export const FieldsEditor = (props: Props) => {
  const { value, disabled, onApply } = props
  const rows = useMemo(() => fieldRows(value) ?? [], [value])
  const renderCell = useCallback((row: FieldRow, key: string) => {
    const field = row.cells.find((item) => item.path.slice(-1)[0] === key)
    return field ? <RowValue row={row} field={field} value={value} onApply={onApply} disabled={disabled} />
      : <Typography.Text type="secondary">未设置</Typography.Text>
  }, [disabled, onApply, value])
  const columns = useMemo<TableColumnsType<FieldRow>>(() => [
    { title: '字段', dataIndex: 'key', width: 160 },
    { title: '显示名称', key: 'label', width: 220, render: (_value, row) => renderCell(row, 'label') },
    { title: '基础填写要求', key: 'requirement', width: 130,
      render: (_value, row) => <RequirementValue row={row} value={value} onApply={onApply} disabled={disabled} /> },
    { title: '说明', key: 'meaning', render: (_value, row) => renderCell(row, 'business_meaning') },
  ], [disabled, onApply, renderCell, value])
  const details = useCallback((row: FieldRow) => <section aria-label={row.key + '的设置'} className="instance-administration__field-settings">
    {fieldSections(row).map((section) => <section key={section.key} className="instance-administration__field-section">
      {section.label ? <Typography.Title level={5}>{section.label}</Typography.Title> : null}
      <div className="instance-administration__field-details">
        {section.fields.map(({ field, label }) => <div key={field.id} className="instance-administration__field-detail">
          <Typography.Text>{label}</Typography.Text>
          <RowValue row={row} field={field} label={label} value={value} onApply={onApply} disabled={disabled} />
        </div>)}
      </div>
    </section>)}
  </section>, [disabled, onApply, value])
  const expandable = useMemo(() => ({ expandedRowRender: details, rowExpandable: (row: FieldRow) => row.details.length > 0,
    columnTitle: '详情', columnWidth: 56 }), [details])
  return <Table<FieldRow> aria-label="字段配置" className="instance-administration__fields-table" rowKey="key" columns={columns} dataSource={rows} size="small"
    pagination={false} scroll={SCROLL} locale={LOCALE} expandable={expandable} />
}
